'use client'

import { useRef, useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getNotificationPreferences, updateNotificationPreferences } from './notification-actions'
import type { NotificationPreferences } from './types'

export type PreferencesSaveState = 'idle' | 'pending' | 'saved' | 'error'

const DEBOUNCE_MS = 500
const SAVED_BADGE_MS = 2000

export function useNotificationPreferences() {
  const queryClient = useQueryClient()
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // All not-yet-flushed updates, merged. A single shared timer used to hold
  // only the LAST toggled key, silently dropping earlier ones (BUG: toggling
  // two prefs within 500ms lost the first).
  const pendingRef = useRef<Partial<NotificationPreferences>>({})
  // Server-truth snapshot taken before the first optimistic update of the
  // current batch, so an error can revert the whole batch.
  const baselineRef = useRef<NotificationPreferences | null>(null)

  const [saveState, setSaveState] = useState<PreferencesSaveState>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const result = await getNotificationPreferences()
      if (result.success && result.data) return result.data
      return null
    },
  })

  const mutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const result = await updateNotificationPreferences(updates)
      // Server actions report failure as { success: false } without throwing;
      // surface it as a real error so onError (revert + message) runs.
      if (!result.success) {
        throw new Error(result.error || 'Failed to save preferences')
      }
      return result
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notification-preferences'] })
      const prev = baselineRef.current
        ?? queryClient.getQueryData<NotificationPreferences>(['notification-preferences'])
        ?? null
      baselineRef.current = null
      return { prev }
    },
    onSuccess: () => {
      setSaveError(null)
      setSaveState('saved')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => {
        setSaveState((s) => (s === 'saved' ? 'idle' : s))
      }, SAVED_BADGE_MS)
    },
    onError: (err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['notification-preferences'], context.prev)
      }
      setSaveState('error')
      setSaveError(err instanceof Error ? err.message : 'Failed to save preferences')
    },
    onSettled: () => {
      // Reconcile with server truth so a silent drift can never survive
      // until the next full reload.
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] })
    },
  })

  /** Queue one or more preference changes (merged + debounced). */
  const queueUpdates = useCallback(
    (updates: Partial<NotificationPreferences>) => {
      // Snapshot server truth before the first optimistic update of a batch.
      if (!baselineRef.current) {
        baselineRef.current =
          queryClient.getQueryData<NotificationPreferences>(['notification-preferences']) ?? null
      }

      // Optimistic update immediately.
      queryClient.setQueryData<NotificationPreferences>(
        ['notification-preferences'],
        (old) => (old ? { ...old, ...updates } : old),
      )

      pendingRef.current = { ...pendingRef.current, ...updates }
      setSaveState('pending')
      setSaveError(null)

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        const merged = pendingRef.current
        pendingRef.current = {}
        if (Object.keys(merged).length > 0) {
          mutation.mutate(merged)
        }
      }, DEBOUNCE_MS)
    },
    [mutation, queryClient],
  )

  const updatePreference = useCallback(
    (key: keyof NotificationPreferences, value: boolean) => {
      queueUpdates({ [key]: value })
    },
    [queueUpdates],
  )

  return {
    preferences,
    isLoading,
    updatePreference,
    updatePreferences: queueUpdates,
    isSaving: mutation.isPending,
    saveState,
    saveError,
  }
}
